/**
 * "Only show posts from…" — an audience FILTER, as a block.
 *
 * Removes posts whose author the reader's own graph does not vouch for, and
 * says up front what to do when that leaves nothing. It pins inside a Page
 * Layer and nowhere else: a filter is applied per reader at serve time, so it
 * cannot sit in the match tree with the conditions the firehose evaluates.
 *
 * Several filters directly in a layer are ALL required. That is the wrong
 * default for graph membership more often than not — `follows2` excludes
 * people you already follow, so "follows AND follows2" is empty by
 * construction — which is what the Audience Group block is for: pin filters
 * inside an "any of these" group instead.
 *
 * "If nothing matches" lives on the LAYER, not here: the serve path composes
 * one answer for the page, and saying it once is honest where saying it per
 * filter was not.
 *
 * Every rule enforced here is the serve path's, mirrored: a threshold only on
 * a countable facet (a "how many" on `follows` is a category error all three
 * parsers refuse), the facet budget of six distinct signals per layer, and the
 * cap on filter leaves, both checked at drop time in `canPlaceInShape`.
 */

import { FilterIcon } from "lucide-react";
import { useCallback } from "react";
import {
  Editor,
  RecordProps,
  T,
  TLShape,
  TLShapeUtilCanBindOpts,
} from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasNumberInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  AUDIENCE_BLOCK_MIN_W,
  layerFacets,
  layerFilterLeaves,
  rootLayerOf,
} from "@/components/canvas-editor/layers";
import {
  NodeDocsDescription,
  NodeDocsHeader,
  useExampleShape,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  AudienceFilterProps,
  AudienceFilterShape,
  audienceFilterType,
  audienceLogicType,
  Dimensions,
  isAudienceLogicShape,
  isLayerShape,
  layerType,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import {
  blendableLensFacetOptions,
  blendableLensFacets,
  isCountableFacet,
  MAX_AUDIENCE_FILTER_LEAVES,
  MAX_AUDIENCE_MIN_COUNT,
  MAX_LENS_BLEND_ENTRIES,
  type BlendableLensFacet,
} from "@/lib/lens";

function AudienceFilterDocs({ editor }: { editor: Editor }) {
  const example = useExampleShape(new AudienceFilterShapeUtil(editor), {
    w: AUDIENCE_BLOCK_MIN_W,
  });
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>{AudienceFilterShapeUtil.docs.title}</NodeDocsHeader>
      <NodeDocsDescription>
        Keeps only posts whose author is in this part of the reader&rsquo;s own
        graph, and removes the rest. Pin it inside a Page Layer. Unlike a boost,
        a filter changes <strong>which</strong> posts are fetched, not just their
        order.
      </NodeDocsDescription>
      <NodeDocsDescription>
        Several filters directly in a layer must <strong>all</strong> match;
        put them in an Audience Group set to &ldquo;any of these&rdquo; to
        keep a post when its author is in any one of them. The layer decides
        what happens when nothing matches — check the preview as a reader who
        is not you.
      </NodeDocsDescription>
      <AudienceFilter
        {...(example as unknown as React.ComponentProps<typeof AudienceFilter>)}
        isBound
        className="w-full relative!"
      />
    </div>
  );
}

export class AudienceFilterTool extends ToolStateNode {
  static override id = audienceFilterType;

  static override uiToolDef = (editor: Editor) => ({
    id: audienceFilterType,
    icon: FilterIcon,
    label: AudienceFilterShapeUtil.docs.title,
    defaultColor: AudienceFilterShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(audienceFilterType),
    docs: () => <AudienceFilterDocs editor={editor} />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;
    this.editor.createShape({
      type: audienceFilterType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });
    this.editor.setCurrentTool("select");
  }
}

export class AudienceFilterShapeUtil extends BindableUtil<AudienceFilterShape> {
  static override migrations = createMigrations(audienceFilterType);
  static override type = audienceFilterType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Only Show Posts From",
    description:
      "Keep only posts from a part of the reader's own graph; remove the rest.",
  };

  override getType() {
    return audienceFilterType;
  }

  // No `resultState`: Debug colours match-tree nodes from the audit, and
  // `clearAuditLog` writes the key onto every shape whose util declares it.
  // An audience block is not evaluated by Debug and must not be coloured.
  static override props: RecordProps<AudienceFilterShape> = {
    w: T.number,
    minW: T.number,
    h: T.number,
    isCollapsed: T.boolean.optional(),
    isReadOnly: T.boolean.optional(),
    metadata: metadataType,
    facet: T.literalEnum(...blendableLensFacets),
    min: T.number.optional(),
    // 🔴 TOLERATED, NOT READ. The first cut of this block carried its own
    // `on_empty`; it moved to the layer the same day. A snapshot saved in
    // between still holds it, and a `T.object` that does not declare it
    // refuses the whole record — tldraw does not degrade a shape validation
    // failure, it white-screens the canvas (see #157 for the Feed's version
    // of this on 2026-09-14). Declared so those snapshots open; ignored.
    on_empty: T.literalEnum("strict", "unfiltered").optional(),
  };

  override getDefaultProps() {
    return {
      w: AUDIENCE_BLOCK_MIN_W,
      minW: AUDIENCE_BLOCK_MIN_W,
      h: MIN_H,
      facet: "follows" as BlendableLensFacet,
      min: undefined,
      metadata: undefined,
    };
  }

  /** Into a layer or an audience group, and into nothing else — not the Feed, not a Logic node. */
  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<AudienceFilterShape>) {
    if (bindingType === "arrow") return true;
    return (
      bindingType === "layout" &&
      (fromShapeType === layerType || fromShapeType === audienceLogicType) &&
      toShapeType === audienceFilterType
    );
  }

  /**
   * Refuse the drop that would put the layer over its facet budget or its
   * leaf cap. Counted at the LAYER the container ultimately belongs to, since
   * that is where the blobs are fetched; a group with no layer above it has
   * no budget to exceed yet.
   */
  override canPlaceInShape(container: TLShape, shape: TLShape) {
    if (!super.canPlaceInShape(container, shape)) return false;
    if (!isLayerShape(container) && !isAudienceLogicShape(container)) return false;
    const layer = isLayerShape(container) ? container : rootLayerOf(this.editor, container);
    if (!layer) return true;
    const facets = layerFacets(this.editor, layer, shape.id);
    facets.add((shape as AudienceFilterShape).props.facet);
    if (facets.size > MAX_LENS_BLEND_ENTRIES) return false;
    const leaves = layerFilterLeaves(this.editor, layer).filter((l) => l.id !== shape.id).length;
    return leaves + 1 <= MAX_AUDIENCE_FILTER_LEAVES;
  }

  override component(shape: AudienceFilterShape) {
    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);
    return (
      <AudienceFilter
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={Boolean(rootLayerOf(this.editor, shape))}
        isSelected={isSelected}
      />
    );
  }
}

function AudienceFilter({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected?: boolean;
  isBound: boolean;
  shape: AudienceFilterShape;
  className?: string;
  updateShape: (shape: AudienceFilterShape, props: Partial<AudienceFilterProps>) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => updateShape(shape, dimensions),
    [shape, updateShape],
  );
  const countable = isCountableFacet(shape.props.facet);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        pattern={shape.props.metadata?.pattern}
        className={className}
        variant={shape.props.metadata?.color || "green"}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={AudienceFilterShapeUtil.docs} />
        <BlockBody className="flex-col gap-2 p-3">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-sm">posts from</span>
            <CanvasSimpleSelect
              aria-label="Filter audience"
              value={shape.props.facet}
              options={blendableLensFacetOptions}
              className="flex-1"
              onValueChange={(v) => {
                const facet = v as BlendableLensFacet;
                // A threshold cannot survive a move to a boolean signal — all
                // three parsers refuse the whole setting over one.
                updateShape(shape, {
                  facet,
                  min: isCountableFacet(facet) ? shape.props.min : undefined,
                });
              }}
            />
          </div>

          {countable && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground shrink-0 text-sm">
                reached by at least
              </span>
              <CanvasNumberInput
                aria-label={`Minimum count for ${shape.props.facet}`}
                value={shape.props.min ?? 1}
                minValue={1}
                maxValue={MAX_AUDIENCE_MIN_COUNT}
                className="w-20"
                onValueChange={(v) =>
                  updateShape(shape, {
                    min: Number.isFinite(v) && v >= 1 ? Math.floor(v) : undefined,
                  })
                }
              />
              <span className="text-muted-foreground text-sm">
                {shape.props.facet === "domain"
                  ? "of this feed's authors"
                  : "of the people they follow"}
              </span>
            </div>
          )}

          {!isBound && (
            <p className="text-muted-foreground text-xs">
              Drag this into a Page Layer, or into an Audience Group inside one, to apply it.
            </p>
          )}
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
